import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, from, mergeMap, fromEvent, throttleTime, filter, map, takeUntil } from 'rxjs';
import { isEmpty } from '../utils/common.utils';

/**
 * Service for tracking user activity and idle status
 * Monitors DOM events (keydown, click, wheel, mousemove) and storage changes
 * Maintains last active date across browser tabs via localStorage
 */
@Injectable({
  providedIn: 'root',
})
export class IdleCheckService implements OnDestroy {
  private readonly recordTimeoutMs: number = 500;
  private readonly localStorageKey: string = '__lastActive';
  private readonly userActivityEvents: string[] = ['keydown', 'click', 'wheel', 'mousemove'];
  private readonly unSub$ = new Subject<void>();

  private lastActive: BehaviorSubject<Date>;
  public readonly lastActive$: Observable<Date>;

  constructor() {
    try {
      const lastActiveDate = this.getLastActiveFromLocalStorage() ?? new Date();
      this.lastActive = new BehaviorSubject<Date>(lastActiveDate);
      this.lastActive$ = this.lastActive.asObservable();
    } catch (error) {
      console.error('Error initializing IdleCheckService:', error);
      this.lastActive = new BehaviorSubject<Date>(new Date());
      this.lastActive$ = this.lastActive.asObservable();
    }
  }

  /**
   * Initializes user activity tracking
   * Subscribes to DOM events and cross-tab storage changes
   * Must be called once after service instantiation
   * Cleans up all subscriptions on component/service destroy
   * @returns void
   */
  public setUp(): void {
    try {
      this.setupUserActivityListener();
      this.setupStorageListener();
    } catch (error) {
      console.error('Error in setUp:', error);
    }
  }

  /**
   * Retrieves the current last active date
   * Returns the most recent date from BehaviorSubject
   * @returns Current last active date or current date if not available
   */
  public getLastActiveDate(): Date {
    try {
      const activeDate = this.lastActive?.value;
      return !isEmpty(activeDate) ? activeDate : new Date();
    } catch (error) {
      console.error('Error in getLastActiveDate:', error);
      return new Date();
    }
  }

  /**
   * Lifecycle hook - cleans up all subscriptions and subjects
   * Called automatically on service/component destroy
   */
  ngOnDestroy(): void {
    try {
      this.unSub$.next();
      this.unSub$.complete();
      if (this.lastActive) {
        this.lastActive.complete();
      }
    } catch (error) {
      console.error('Error in ngOnDestroy:', error);
    }
  }

  /**
   * Sets up listener for user activity events
   * Monitors keydown, click, wheel, mousemove with throttling
   * Updates last active date and localStorage on activity
   * @private
   */
  private setupUserActivityListener(): void {
    try {
      if (isEmpty(this.userActivityEvents)) {
        console.warn('User activity events list is empty');
        return;
      }

      from(this.userActivityEvents)
        .pipe(
          mergeMap((event) => fromEvent(document, event)),
          throttleTime(this.recordTimeoutMs),
          takeUntil(this.unSub$)
        )
        .subscribe({
          next: () => {
            try {
              this.recordLastActiveDate();
            } catch (error) {
              console.error('Error recording last active date:', error);
            }
          },
          error: (error) => {
            console.error('Error in user activity listener:', error);
          },
        });
    } catch (error) {
      console.error('Error in setupUserActivityListener:', error);
    }
  }

  /**
   * Sets up listener for cross-tab storage changes
   * Detects when other tabs update the last active date
   * Synchronizes last active date across all tabs
   * @private
   */
  private setupStorageListener(): void {
    try {
      fromEvent<StorageEvent>(window, 'storage')
        .pipe(
          filter((event) => this.isValidStorageEvent(event)),
          map((event) => this.parseStorageEventDate(event)),
          filter((date) => !isEmpty(date)),
          takeUntil(this.unSub$)
        )
        .subscribe({
          next: (newDate) => {
            try {
              if (!isEmpty(newDate)) {
                this.lastActive.next(newDate);
              }
            } catch (error) {
              console.error('Error updating last active from storage:', error);
            }
          },
          error: (error) => {
            console.error('Error in storage listener:', error);
          },
        });
    } catch (error) {
      console.error('Error in setupStorageListener:', error);
    }
  }

  /**
   * Records current time as last active
   * Updates both localStorage and BehaviorSubject
   * Used as callback for user activity events
   * @private
   */
  private recordLastActiveDate(): void {
    try {
      const currentDate = new Date();
      if (isEmpty(currentDate)) {
        console.warn('Current date is invalid');
        return;
      }

      localStorage.setItem(this.localStorageKey, currentDate.toString());
      this.lastActive.next(currentDate);
    } catch (error) {
      console.error('Error in recordLastActiveDate:', error);
    }
  }

  /**
   * Retrieves last active date from localStorage
   * Parses stored date string and validates format
   * Returns null if value doesn't exist or is invalid
   * @private
   * @returns Parsed date from localStorage or null
   */
  private getLastActiveFromLocalStorage(): Date | null {
    try {
      const valueFromStorage = localStorage.getItem(this.localStorageKey);
      if (isEmpty(valueFromStorage)) {
        return null;
      }

      const parsedDate = new Date(valueFromStorage);
      if (isNaN(parsedDate.getTime())) {
        console.warn('Invalid date format in localStorage:', valueFromStorage);
        return null;
      }

      return parsedDate;
    } catch (error) {
      console.error('Error in getLastActiveFromLocalStorage:', error);
      return null;
    }
  }

  /**
   * Validates if storage event is relevant to idle tracking
   * Checks if event targets correct localStorage key with valid new value
   * @private
   * @param event - Storage event to validate
   * @returns True if event is valid and relevant
   */
  private isValidStorageEvent(event: StorageEvent): boolean {
    try {
      return (
        event.storageArea === localStorage &&
        event.key === this.localStorageKey &&
        !isEmpty(event.newValue)
      );
    } catch (error) {
      console.error('Error in isValidStorageEvent:', error);
      return false;
    }
  }

  /**
   * Parses date from storage event's new value
   * Safely converts string to Date object
   * @private
   * @param event - Storage event with new value
   * @returns Parsed date or invalid Date if parsing fails
   */
  private parseStorageEventDate(event: StorageEvent): Date {
    try {
      if (isEmpty(event?.newValue)) {
        return new Date(NaN);
      }

      return new Date(event.newValue);
    } catch (error) {
      console.error('Error in parseStorageEventDate:', error);
      return new Date(NaN);
    }
  }

  /**
   * Checks if value is empty, null, undefined, or invalid
   * Handles strings, objects, arrays, Dates
   * Used for all empty validation in idle tracking
   * @private
   * @param value - Any value to check
   * @returns True if value is considered empty
   */
  private isEmpty(value: any): boolean {
    if (value === null || value === undefined) {
      return true;
    }

    if (typeof value === 'string') {
      return value.trim().length === 0;
    }

    if (Array.isArray(value)) {
      return value.length === 0;
    }

    if (value instanceof Date) {
      return isNaN(value.getTime());
    }

    if (typeof value === 'object') {
      return Object.keys(value).length === 0;
    }

    return false;
  }
}
